// Force clear authentication data from localStorage
localStorage.removeItem('supabase_user')
localStorage.removeItem('supabase_session')
localStorage.removeItem('userId')
localStorage.removeItem('userEmail')
localStorage.removeItem('userFirstName')
localStorage.removeItem('userLastName')
localStorage.removeItem('userDisplayName')
localStorage.removeItem('signUpEmail')
localStorage.removeItem('loggedOut')
localStorage.removeItem('lastUserId')

import type { Session, User } from '@supabase/supabase-js'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase, isOnline } from '../utils/supabase'

export interface AuthState {
  session: Session | null
  user: User | any | null // Allow 'any' for our custom user object
  token: string | null
  loading: boolean
  error: string | null
  initialized: boolean
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const state = ref<AuthState>({
    session: null,
    user: null,
    token: null, // Add token to state for custom auth
    loading: false,
    error: null,
    initialized: false
  })

  // Initialize from localStorage on store creation
  const initFromLocalStorage = () => {
    try {
      // Try to get user and session from localStorage
      const storedUser = localStorage.getItem('supabase_user')
      const storedSession = localStorage.getItem('supabase_session')
      
      if (storedUser && storedSession) {
        const parsedSession = JSON.parse(storedSession);
        state.value.user = JSON.parse(storedUser);
        state.value.session = parsedSession;
        state.value.token = parsedSession?.access_token ?? null;
        
        // Set userId in localStorage for other stores to use
        if (state.value.user?.id) {
          localStorage.setItem('userId', state.value.user.id)
        }
        
        console.log('Auth initialized from localStorage')
      }
    } catch (error) {
      console.error('Error initializing from localStorage:', error)
    }
  }
  
  // Call initialization
  initFromLocalStorage()

  // Computed properties
  const isAuthenticated = computed(() => !!state.value.user)
  const userRole = computed(() => (state.value.user?.user_metadata?.role || 'user').toLowerCase().trim())
  const isTier1 = computed(() => ['system administrator', 'admin', 'administrator', 'administator', 'head office', 'super admin'].includes(userRole.value))
  const isTier2 = computed(() => ['depot admin', 'depot administrator', 'depot_admin', 'depotadmin', 'supervisor'].includes(userRole.value))
  const isTier3 = computed(() => ['clerk'].includes(userRole.value))
  const isTier4 = computed(() => ['fuel attendant'].includes(userRole.value))
  const hasGlobalAccess = computed(() => isTier1.value)
  const hasAdminAccess = computed(() => isTier1.value || isTier2.value)
  
  // Initialize auth state from Supabase and localStorage
  const initAuth = async () => {
    state.value.loading = true
    state.value.error = null
    
    try {
      console.log('Initializing auth state...')
      
      // First try to get from localStorage
      const storedSession = localStorage.getItem('supabase_session')
      const storedUser = localStorage.getItem('supabase_user')
      
      if (storedSession && storedUser) {
        const parsedSession = JSON.parse(storedSession);
        state.value.session = parsedSession;
        state.value.user = JSON.parse(storedUser);
        state.value.token = parsedSession?.access_token ?? null;
        state.value.initialized = true;
        
        // Set userId in localStorage for other stores to use
        if (state.value.user?.id) {
          localStorage.setItem('userId', state.value.user.id)
        }
        
        console.log('Auth state initialized from localStorage')
      }
      
      // Try to get the current session from Supabase if online
      if (isOnline()) {
        try {
          const { data, error } = await supabase.auth.getSession()
          
          if (error) {
            console.warn('Error getting session from Supabase, using localStorage data:', error)
          } else if (data?.session) {
            // Update session and user in state
            state.value.session = data.session
            state.value.user = data.session.user
            state.value.token = data.session.access_token
            state.value.initialized = true
            
            // Cache in localStorage
            localStorage.setItem('supabase_session', JSON.stringify(data.session))
            localStorage.setItem('supabase_user', JSON.stringify(data.session.user))
            
            // Set userId in localStorage for other stores to use
            if (state.value.user?.id) {
              localStorage.setItem('userId', state.value.user.id)
            }
            
            console.log('Auth state initialized from Supabase')
          }
        } catch (supabaseError) {
          console.warn('Failed to connect to Supabase, using localStorage data:', supabaseError)
          // Continue with localStorage data if available
        }
      } else {
        console.log('Offline mode: using localStorage for auth')
      }
      
      // If we still don't have a user but have an email in localStorage, create a fallback user
      if (!state.value.user && localStorage.getItem('userEmail')) {
        const email = localStorage.getItem('userEmail')
        const userId = localStorage.getItem('userId')
        
        if (email && userId) {
          // Create a minimal user object
          state.value.user = {
            id: userId,
            email: email,
            user_metadata: {
              email: email,
              first_name: localStorage.getItem('userFirstName') || '',
              last_name: localStorage.getItem('userLastName') || '',
              display_name: localStorage.getItem('userDisplayName') || email.split('@')[0] || 'User'
            }
          }
          state.value.initialized = true
          
          // Cache in localStorage
          localStorage.setItem('supabase_user', JSON.stringify(state.value.user))
          
          console.log('Created fallback user from localStorage data')
        }
      }
      
      // Mark as initialized even if we don't have a user
      state.value.initialized = true
      
      return { 
        success: true, 
        user: state.value.user, 
        session: state.value.session 
      }
    } catch (error) {
      console.error('Error initializing auth:', error)
      state.value.error = error instanceof Error ? error.message : 'Unknown error'
      
      // Mark as initialized even if there was an error
      state.value.initialized = true
      
      return { success: false, error: state.value.error }
    } finally {
      state.value.loading = false
      console.log('Auth state initialization completed')
    }
  }

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    state.value.loading = true
    state.value.error = null
    
    try {
      console.log(`Attempting to sign in with email: ${email}`)
      
      // Only attempt sign in if online
      if (!isOnline()) {
        throw new Error('Cannot sign in while offline')
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      
      if (error) throw error
      
      if (data.user && data.session) {
        console.log('Sign in response:', data)
        
        // Check if email is verified
        if (data.user.email_confirmed_at) {
          console.log('User email verified, setting session and user data')
          
          // Store user and session
          state.value.user = data.user
          state.value.session = data.session
          state.value.token = data.session.access_token
          
          // Cache in localStorage for offline access
          localStorage.setItem('supabase_user', JSON.stringify(data.user))
          localStorage.setItem('supabase_session', JSON.stringify(data.session))
          localStorage.setItem('userId', data.user.id)
          localStorage.setItem('userEmail', data.user.email || '')
          
          // Store user metadata fields separately for easy access
          if (data.user.user_metadata) {
            if (data.user.user_metadata.first_name) {
              localStorage.setItem('userFirstName', data.user.user_metadata.first_name)
            }
            if (data.user.user_metadata.last_name) {
              localStorage.setItem('userLastName', data.user.user_metadata.last_name)
            }
            if (data.user.user_metadata.display_name) {
              localStorage.setItem('userDisplayName', data.user.user_metadata.display_name)
            }
          }
          
          return { success: true, user: data.user }
        } else {
          console.log('User email not verified')
          return { success: false, error: 'Please verify your email before signing in' }
        }
      }
      
      throw new Error('Sign in failed: No user or session returned')
    } catch (error) {
      console.error('Sign in error:', error)
      state.value.error = error instanceof Error ? error.message : 'Unknown error'
      return { success: false, error: state.value.error }
    } finally {
      state.value.loading = false
    }
  }

  // Sign in with a custom token (for driver login)
  const signInWithCustomToken = async (token: string) => {
    state.value.loading = true
    state.value.error = null
    try {
      console.log('Attempting to sign in with custom token')
      if (!isOnline()) {
        throw new Error('Cannot sign in while offline')
      }

      // Manually decode the JWT to prevent race conditions with the router guard.
      // This synchronously updates the user state before the session is fully set.
      const parseJwt = (token: string) => {
        try {
          const base64Url = token.split('.')[1];
          if (!base64Url) return null;
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split('')
              .map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
              })
              .join('')
          );
          return JSON.parse(jsonPayload);
        } catch (e) {
          console.error('Failed to parse JWT:', e);
          return null;
        }
      };

      const decodedToken = parseJwt(token);
      
      // DEBUG: Log the entire decoded token to inspect its structure
      console.log('DEBUG: Decoded JWT payload:', decodedToken);

      if (!decodedToken || !decodedToken.sub) {
        throw new Error('Invalid custom token received.');
      }

      // Ensure 'iat' is a number before creating a Date object
      const iat = decodedToken.iat ? Number(decodedToken.iat) : Date.now() / 1000;
      if (isNaN(iat)) {
        throw new Error(`Invalid 'iat' timestamp in token: ${decodedToken.iat}`);
      }

      // Create the user object from the token payload.
      const user = {
        id: decodedToken.sub,
        aud: decodedToken.aud,
        role: decodedToken.role,
        email: decodedToken.email, // May be undefined, that's ok
        user_metadata: decodedToken.user_metadata || {},
        app_metadata: { provider: 'custom' },
        created_at: new Date(iat * 1000).toISOString(),
        updated_at: new Date(iat * 1000).toISOString(),
      };

      // Immediately update the state
      state.value.user = user;
      state.value.token = token;
      
      // For custom driver auth, we manage the session manually and do not use supabase.auth.setSession.
      // We just store the token and user object in our state.
      console.log('DEBUG: Bypassing setSession for custom driver auth. Storing token manually.');

      // Create a mock session object for consistency in the state
      const session = {
        access_token: token,
        refresh_token: token, // Using the same token for simplicity
        token_type: 'bearer',
        user: user as any, // Cast to avoid type conflicts with Supabase User type
      };

      // Update the state
      state.value.session = session as any;
      state.value.initialized = true;

      // Cache everything in localStorage
      localStorage.setItem('supabase_user', JSON.stringify(user));
      localStorage.setItem('supabase_session', JSON.stringify(session));
      localStorage.setItem('custom_token', token);
      localStorage.setItem('userId', user.id);
      if (user.email) localStorage.setItem('userEmail', user.email);
      if (user.user_metadata.first_name) localStorage.setItem('userFirstName', user.user_metadata.first_name);
      if (user.user_metadata.last_name) localStorage.setItem('userLastName', user.user_metadata.last_name);
      if (user.user_metadata.display_name) localStorage.setItem('userDisplayName', user.user_metadata.display_name);

      console.log('Custom token sign in successful:', { user, session });
      return { success: true, user };
    } catch (error) {
      console.error('Sign in with custom token error:', error)
      state.value.error = error instanceof Error ? error.message : 'Unknown error'
      return { success: false, error: state.value.error }
    } finally {
      state.value.loading = false
    }
  }

  // Sign up with email and password
  const signUp = async (email: string, password: string, userData: any = {}) => {
    state.value.loading = true
    state.value.error = null
    
    try {
      console.log(`Attempting to sign up with email: ${email}`)
      
      // Only attempt sign up if online
      if (!isOnline()) {
        throw new Error('Cannot sign up while offline')
      }
      
      // Prepare user metadata
      const metadata = {
        ...userData,
        display_name: userData.display_name || userData.first_name || email.split('@')[0],
        full_name: userData.full_name || 
                  (userData.first_name && userData.last_name) ? 
                  `${userData.first_name} ${userData.last_name}`.trim() : 
                  email.split('@')[0]
      }
      
      // Get the current origin safely
      const origin = window.location.origin || 'http://localhost:3000'
      const redirectTo = `${origin}/auth/callback`
      
      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          data: metadata,
          emailRedirectTo: redirectTo
        }
      })
      
      if (error) throw error
      
      if (data.user) {
        console.log('Sign up successful, verification email sent')
        
        // Store email for verification page
        localStorage.setItem('signUpEmail', email)
        
        // Store user metadata fields separately for easy access
        if (metadata.first_name) localStorage.setItem('userFirstName', metadata.first_name)
        if (metadata.last_name) localStorage.setItem('userLastName', metadata.last_name)
        if (metadata.display_name) localStorage.setItem('userDisplayName', metadata.display_name)
        
        return { success: true, user: data.user }
      }
      
      throw new Error('Sign up failed: No user returned')
    } catch (error) {
      console.error('Sign up error:', error)
      state.value.error = error instanceof Error ? error.message : 'Unknown error'
      return { success: false, error: state.value.error }
    } finally {
      state.value.loading = false
    }
  }

  // Sign out
  const signOut = async () => {
    state.value.loading = true
    state.value.error = null
    
    try {
      console.log('Signing out')
      
      // Attempt to sign out from Supabase if online
      if (isOnline()) {
        const { error } = await supabase.auth.signOut()
        if (error) {
          console.warn('Error signing out from Supabase:', error)
        }
      }
      
      // Clear state regardless of online status
      state.value.user = null
      state.value.session = null
      state.value.token = null
      
      // Keep a copy of the user ID for potential offline operations
      const userId = localStorage.getItem('userId')
      
      // Clear auth-related localStorage items
      localStorage.removeItem('supabase_user')
      localStorage.removeItem('supabase_session')
      
      // Keep userId for offline mode but mark as logged out
      if (userId) {
        localStorage.setItem('lastUserId', userId)
        localStorage.setItem('loggedOut', 'true')
      }
      
      return { success: true }
    } catch (error) {
      console.error('Sign out error:', error)
      state.value.error = error instanceof Error ? error.message : 'Unknown error'
      return { success: false, error: state.value.error }
    } finally {
      state.value.loading = false
    }
  }

  // Update user metadata
  const updateUserMetadata = async (metadata: any) => {
    state.value.loading = true
    state.value.error = null
    
    try {
      console.log('Updating user metadata:', metadata)
      
      // Store in localStorage first for offline resilience
      Object.entries(metadata).forEach(([key, value]) => {
        if (typeof value === 'string') {
          localStorage.setItem(`user${key.charAt(0).toUpperCase() + key.slice(1)}`, value)
        }
      })
      
      // Only attempt to update Supabase if online
      if (isOnline()) {
        const { data, error } = await supabase.auth.updateUser({
          data: metadata
        })
        
        if (error) throw error
        
        if (data.user) {
          // Update local state
          state.value.user = data.user
          
          // Update localStorage
          localStorage.setItem('supabase_user', JSON.stringify(data.user))
          
          console.log('User metadata updated successfully')
          return { success: true, user: data.user }
        }
      } else {
        console.log('Offline mode: metadata saved locally only')
        
        // Update the local user object
        if (state.value.user && state.value.user.user_metadata) {
          state.value.user.user_metadata = {
            ...state.value.user.user_metadata,
            ...metadata
          }
          
          // Update localStorage
          localStorage.setItem('supabase_user', JSON.stringify(state.value.user))
        }
        
        return { success: true, offline: true }
      }
      
      return { success: true, offline: !isOnline() }
    } catch (error) {
      console.error('Error updating user metadata:', error)
      state.value.error = error instanceof Error ? error.message : 'Unknown error'
      return { success: false, error: state.value.error }
    } finally {
      state.value.loading = false
    }
  }

  // Reset password
  const resetPassword = async (email: string) => {
    state.value.loading = true
    state.value.error = null
    
    try {
      console.log(`Sending password reset email to: ${email}`)
      
      // Only attempt if online
      if (!isOnline()) {
        throw new Error('Cannot reset password while offline')
      }
      
      // Get the current origin safely
      const origin = window.location.origin || 'http://localhost:3000'
      const redirectTo = `${origin}/auth/reset-password`
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectTo
      })
      
      if (error) throw error
      
      return { success: true }
    } catch (error) {
      console.error('Password reset error:', error)
      state.value.error = error instanceof Error ? error.message : 'Unknown error'
      return { success: false, error: state.value.error }
    } finally {
      state.value.loading = false
    }
  }

  // Change password
  const changePassword = async (password: string) => {
    state.value.loading = true
    state.value.error = null
    
    try {
      console.log('Changing password')
      
      // Only attempt if online
      if (!isOnline()) {
        throw new Error('Cannot change password while offline')
      }
      
      const { data, error } = await supabase.auth.updateUser({ password })
      
      if (error) throw error
      
      if (data.user) {
        console.log('Password changed successfully')
        return { success: true, user: data.user }
      }
      
      throw new Error('Password change failed: No user returned')
    } catch (error) {
      console.error('Password change error:', error)
      state.value.error = error instanceof Error ? error.message : 'Unknown error'
      return { success: false, error: state.value.error }
    } finally {
      state.value.loading = false
    }
  }

  // Resend verification email
  const resendVerificationEmail = async (email: string) => {
    state.value.loading = true
    state.value.error = null
    
    try {
      console.log(`Resending verification email to: ${email}`)
      
      // Only attempt if online
      if (!isOnline()) {
        throw new Error('Cannot resend verification email while offline')
      }
      
      // Get the current origin safely
      const origin = window.location.origin || 'http://localhost:3000'
      const redirectTo = `${origin}/auth/callback`
      
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: redirectTo
        }
      })
      
      if (error) throw error
      
      return { success: true }
    } catch (error) {
      console.error('Error resending verification email:', error)
      state.value.error = error instanceof Error ? error.message : 'Unknown error'
      return { success: false, error: state.value.error }
    } finally {
      state.value.loading = false
    }
  }

  return {
    // State
    state,
    user: computed(() => state.value.user),
    session: computed(() => state.value.session),
    loading: computed(() => state.value.loading),
    error: computed(() => state.value.error),
    initialized: computed(() => state.value.initialized),
    isAuthenticated,
    userRole,
    isTier1,
    isTier2,
    isTier3,
    isTier4,
    hasGlobalAccess,
    hasAdminAccess,
    
    // Actions
    initFromLocalStorage,
    initAuth,
    signIn,
    signInWithCustomToken,
    signUp,
    signOut,
    updateUserMetadata,
    resetPassword,
    changePassword,
    resendVerificationEmail
  }
})